<template>
  <div class="warranties-page">
    <div class="warranties-container">
      <h1 class="warranties-title">{{ $t('warranties.title').toUpperCase() }}</h1>
      <v-divider class="mb-4"></v-divider>

      <div class="d-flex flex-wrap mb-4">
        <button
          class="btn btn-honda-plus mr-2 text-no-wrap mb-1 btn-radius btn-filter"
          :class="{'btn-disabled': statusFilter !== 'in_progress'}"
          @click="statusFilter = 'in_progress'"
          :disabled="loading">
          <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
          {{ $t('warranties.status.in-progress') }}
        </button>
        <button
          class="btn btn-honda-plus mr-2 text-no-wrap mb-1 btn-radius btn-filter"
          :class="{'btn-disabled': statusFilter !== 'closed'}"
          @click="statusFilter = 'closed'"
          :disabled="loading">
          <i class="fas fa-check-circle" aria-hidden="true"></i>
          {{ $t('warranties.status.closed') }}
        </button>
      </div>

      <v-data-table
        id="warranties-table-list"
        :loading="isLoading"
        :loading-text="$t('warranties.loading')"
        :headers="headers"
        :items="tickets"
        :items-per-page="10"
        class="elevation-0 warranties-table"
        sort-by="acceptanceDate"
        sort-desc
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'fas fa-angle-double-left',
          lastIcon: 'fas fa-angle-double-right',
          prevIcon: 'fas fa-angle-left',
          nextIcon: 'fas fa-angle-right',
          'items-per-page-text': '',
          'items-per-page-options': [10, 25, 50, -1],
        }"
        :no-data-text="$t('warranties.no-data')"
        :no-results-text="$t('warranties.no-data')">
        <template v-slot:header.pipelineStage="{header}">
          <span>{{ header.text }}</span>
          <v-tooltip bottom max-width="350" nudge-bottom="5" nudge-right="100">
            <template v-slot:activator="{on, attrs}">
              <v-icon v-bind="attrs" v-on="on" small class="ml-1 stage-info-icon"> far fa-info-circle </v-icon>
            </template>
            <div class="stage-tooltip-content">
              <p>
                <strong>{{ $t('warranties.stage-tooltip.pending-reception') }}.</strong>
                {{ $t('warranties.stage-tooltip.pending-reception-desc') }}
              </p>
              <p>
                <strong>{{ $t('warranties.stage-tooltip.device-received') }}.</strong>
                {{ $t('warranties.stage-tooltip.device-received-desc') }}
              </p>
              <p>
                <strong>{{ $t('warranties.stage-tooltip.in-review') }}.</strong>
                {{ $t('warranties.stage-tooltip.in-review-desc') }}
              </p>
              <p>
                <strong>{{ $t('warranties.stage-tooltip.pending-billing') }}</strong>
                {{ $t('warranties.stage-tooltip.pending-billing-desc') }}
              </p>
              <p>
                <strong>{{ $t('warranties.stage-tooltip.closed') }}.</strong>
                {{ $t('warranties.stage-tooltip.closed-desc') }}
              </p>
            </div>
          </v-tooltip>
        </template>

        <template #footer.page-text="props">
          {{ props.pageStart }}-{{ props.pageStop }} {{ $t('common.of') }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:item.acceptanceDate="{item}">
          <span class="acceptance-date-cell">{{ formatDate(item.acceptanceDate) }}</span>
        </template>

        <template v-slot:item.vehicleRegNumber="{item}">
          <span>{{ item.vehicleRegNumber || '—' }}</span>
        </template>

        <template v-slot:item.deviceImei="{item}">
          <Imei :value="item.deviceImei" />
        </template>

        <template v-slot:item.pipelineStage="{item}">
          <div class="stage-cell">
            <span :class="['stage-dot', getStageClass(item.pipelineStage)]"></span>
            <span>{{ getStageLabel(item.pipelineStage) }}</span>
          </div>
        </template>

        <template v-slot:item.resolutionStatus="{item}">
          <span>{{ getResolutionStatusLabel(item.resolutionStatus) }}</span>
        </template>
      </v-data-table>

      <div v-if="error" class="text-center text-error mt-4">
        {{ $t('warranties.error') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, ref, watch} from '@vue/composition-api'

import warrantyApi, {WarrantyTicket} from '@/api/warranty.api'
import Imei from '@/components/general/Imei.vue'

export default defineComponent({
  name: 'Warranties',
  components: {
    Imei,
  },
  setup(_, {root}) {
    // Reactive state
    const statusFilter = ref<'in_progress' | 'closed'>('in_progress')
    const tickets = ref<WarrantyTicket[]>([])
    const loading = ref(false)
    const error = ref(false)

    // Computed properties
    const group = computed(() => root.$store.getters.getGroup)
    const dealerId = computed(() => group.value?.dealerId)
    const isLoading = computed(() => loading.value)

    const headers = computed(() => [
      {
        text: root.$t('warranties.headers.acceptance-date'),
        align: 'left',
        value: 'acceptanceDate',
        class: 'text-header',
        width: '18%',
        sortable: true,
      },
      {
        text: root.$t('warranties.headers.vehicle-reg-number'),
        align: 'left',
        value: 'vehicleRegNumber',
        class: 'text-header',
        width: '15%',
        sortable: false,
      },
      {
        text: root.$t('warranties.headers.device-imei'),
        align: 'left',
        value: 'deviceImei',
        class: 'text-header',
        width: '18%',
        sortable: false,
      },
      {
        text: root.$t('warranties.headers.stage'),
        align: 'left',
        value: 'pipelineStage',
        class: 'text-header',
        width: '30%',
        sortable: false,
      },
      {
        text: root.$t('warranties.headers.resolution-status'),
        align: 'left',
        value: 'resolutionStatus',
        class: 'text-header',
        width: '19%',
        sortable: false,
      },
    ])

    // Methods
    const loadTickets = async () => {
      if (!dealerId.value) {
        return
      }

      loading.value = true
      error.value = false

      try {
        const allClaims: WarrantyTicket[] = []
        let after: string | undefined

        // Fetch all pages using cursor-based pagination
        do {
          const response = await warrantyApi.getWarrantyTickets(dealerId.value, {
            status: statusFilter.value,
            limit: 100,
            after,
          })
          allClaims.push(...response.claims)
          after = response.paging?.next?.after
        } while (after)

        tickets.value = allClaims
      } catch (err) {
        console.error('Error loading warranty tickets:', err)
        error.value = true
        tickets.value = []
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateString: string | null): string => {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return dateString
        return date.toLocaleDateString(root.$i18n.locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      } catch {
        return dateString
      }
    }

    const getStageLabel = (stageKey: string | null): string => {
      if (!stageKey) return '-'
      const translated = root.$t(`warranties.stages.${stageKey}`)
      // If translation key not found, $t returns the key itself
      return translated !== `warranties.stages.${stageKey}` ? String(translated) : stageKey
    }

    const getResolutionStatusLabel = (statusKey: string | null): string => {
      if (!statusKey) return '-'
      const translated = root.$t(`warranties.resolution-status.${statusKey}`)
      // If translation key not found, $t returns the key itself
      return translated !== `warranties.resolution-status.${statusKey}` ? String(translated) : statusKey
    }

    const getStageClass = (stageKey: string | null): string => {
      // 'pending-reception' is red, 'closed' is green, others are yellow
      if (stageKey === 'pending-reception') {
        return 'stage-dot--red'
      }
      if (stageKey === 'closed') {
        return 'stage-dot--green'
      }
      return 'stage-dot--yellow'
    }

    // Watchers
    watch(statusFilter, () => {
      loadTickets()
    })

    watch(
      dealerId,
      newVal => {
        if (newVal) {
          loadTickets()
        }
      },
      {immediate: true},
    )

    return {
      // State
      statusFilter,
      tickets,
      loading,
      error,
      isLoading,
      headers,
      // Methods
      formatDate,
      getStageLabel,
      getResolutionStatusLabel,
      getStageClass,
    }
  },
})
</script>

<style lang="scss">
.warranties-page {
  background-color: #f5f5f5;
  min-height: 100%;
  padding: 1.5rem;

  .btn-radius {
    border-radius: 4px !important;
  }

  .btn-filter {
    min-width: auto !important;
    width: auto !important;
    padding: 10px 20px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
    line-height: 1 !important;
  }

  .warranties-container {
    background-color: white;
    border-radius: 4px;
    padding: 1.5rem;
  }

  .warranties-title {
    font-size: 36px;
    font-weight: bold;
    color: #333;
    margin-bottom: 1rem;
    font-family: 'ProximaNovaExCnSbold', sans-serif;
    text-transform: uppercase;
  }

  .warranties-table {
    background-color: white;

    // Column headers styling
    th.text-header {
      font-size: 14px !important;
      font-weight: 600 !important;
    }

    .stage-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .stage-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;

      &--red {
        background-color: #f44336;
      }

      &--yellow {
        background-color: #ffc107;
      }

      &--green {
        background-color: #4caf50;
      }
    }

    .acceptance-date-cell {
      font-weight: 600;
    }

    .stage-info-icon {
      color: #2196f3 !important;
      font-size: 14px !important;
      cursor: pointer;
    }
  }
}

.stage-tooltip-content {
  padding: 4px 0;

  p {
    margin: 0 0 10px 0;
    font-size: 13px;
    line-height: 1.5;
    text-align: left;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.v-tooltip__content {
  &.menuable__content__active {
    background-color: rgba(33, 33, 33, 0.95) !important;
    border-radius: 12px !important;
    padding: 12px 16px !important;
  }
}
</style>
